import React, { useState } from 'react';
import { ArrowRight, CreditCard, Calendar, Target, FileText, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { CALENDLY_DIAGNOSTIC_URL } from '@/lib/config';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { toast } from '@/hooks/use-toast';

const BENEFITS = [
  {
    icon: Target,
    title: "Audit complet",
    description: "Positionnement, concurrence, message",
  },
  {
    icon: FileText,
    title: "Plan d'action",
    description: "Livrable PDF sous 5 jours",
  },
  {
    icon: MessageCircle,
    title: "Session live",
    description: "2h en visio avec Tina",
  },
];

const Diagnostic: React.FC = () => {
  const navigate = useNavigate();
  useScrollReveal();
  const [step, setStep] = useState<'booking' | 'payment'>('booking');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const handleCalendlyEvent = () => {
    setStep('payment');
  };

  const handlePayment = async () => {
    if (!email) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { email, name },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast({
        title: "Erreur de paiement",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Diagnostic Stratégique — Glow Lab"
        description="En 2 heures, je décortique ton positionnement et je te livre un plan d'action sur mesure. Audit, roadmap et recommandations pour 590€."
        url="https://glow-architecte.com/diagnostic"
      />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-12 h-px bg-[hsl(var(--rose-text))]/30 mx-auto mb-6" />
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.1]">
              Diagnostic Stratégique
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl mt-4 text-pretty">
              En 2 heures, je décortique ton positionnement et je te livre un plan d'action sur mesure.
            </p>
            <div className="mt-4 text-[hsl(var(--rose-text))] font-display font-semibold text-2xl">590€</div>
          </div>
        </section>

        {/* Benefits band */}
        <section className="bg-muted py-16">
          <div className="container mx-auto px-4">
            <div className="reveal grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {BENEFITS.map((benefit, i) => (
                <div
                  key={i}
                  className="reveal-child text-center p-6 border border-border rounded-xl bg-card shadow-sm hover-lift transition-all duration-300"
                >
                  <benefit.icon className="w-6 h-6 text-[hsl(var(--rose-text))] mx-auto" strokeWidth={1.5} />
                  <p className="font-display text-xl mt-3 text-foreground">{benefit.title}</p>
                  <p className="text-muted-foreground text-sm mt-1">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stepper + Form */}
        <section className="container mx-auto px-4 py-16 md:py-20">
          {/* Steps indicator */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 text-sm font-medium ${step === 'booking' ? 'text-[hsl(var(--rose-text))]' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${step === 'booking' ? 'bg-[hsl(var(--rose-text))] text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                1
              </div>
              <span className="hidden sm:inline">Choisir un créneau</span>
              <Calendar className="w-4 h-4 sm:hidden" />
            </div>
            <div className="w-8 h-px bg-border" />
            <div className={`flex items-center gap-2 text-sm font-medium ${step === 'payment' ? 'text-[hsl(var(--rose-text))]' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${step === 'payment' ? 'bg-[hsl(var(--rose-text))] text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                2
              </div>
              <span className="hidden sm:inline">Payer</span>
              <CreditCard className="w-4 h-4 sm:hidden" />
            </div>
          </div>

          {/* Step content */}
          <div className="max-w-3xl mx-auto">
            {step === 'booking' ? (
              <div className="bg-muted rounded-2xl border border-border p-6 md:p-8 shadow-sm">
                <h3 className="font-display text-2xl text-foreground text-center mb-6">
                  Choisis ton créneau
                </h3>
                {CALENDLY_DIAGNOSTIC_URL ? (
                  <div className="rounded-lg overflow-hidden border border-border mb-6">
                    <iframe
                      src={CALENDLY_DIAGNOSTIC_URL}
                      width="100%"
                      height="630"
                      frameBorder="0"
                      title="Calendly — Diagnostic Stratégique"
                      className="w-full"
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-background p-12 text-center mb-6">
                    <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-muted-foreground text-sm">
                      Le calendrier de réservation sera disponible très bientôt.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      En attendant, contacte-moi à tina@majoliepeau.com
                    </p>
                  </div>
                )}
                <button
                  onClick={handleCalendlyEvent}
                  className="w-full bg-primary text-primary-foreground font-semibold py-4 rounded-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_4px_20px_hsl(var(--primary)/0.3)]"
                >
                  J'ai choisi mon créneau — Passer au paiement
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="bg-muted rounded-2xl border border-border p-6 md:p-8 shadow-sm">
                <h3 className="font-display text-2xl text-foreground text-center mb-6">
                  Finalise ta réservation
                </h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">
                      Ton prénom
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Marie"
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                      Ton email <span className="text-[hsl(var(--rose-text))]">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="marie@exemple.com"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary/50 transition-colors"
                    />
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={handlePayment}
                    disabled={!email || isLoading}
                    className="w-full bg-primary text-primary-foreground font-semibold py-4 rounded-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_20px_hsl(var(--primary)/0.3)]"
                  >
                    {isLoading ? 'Redirection...' : 'Payer et confirmer (590€)'}
                    {!isLoading && <CreditCard className="w-4 h-4" />}
                  </button>
                  <p className="text-xs text-muted-foreground mt-3">
                    Paiement sécurisé par Stripe
                  </p>
                </div>

                <button
                  onClick={() => setStep('booking')}
                  className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Revenir au choix du créneau
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Diagnostic;
